import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { clearIdentity, stopCoachRecording } from '@/lib/posthog';
import { setSentryUser } from '@/lib/sentry';

export interface CoachSessionTeam {
  id: string;
  name: string;
  tournamentId: string;
  tournamentName?: string;
  organizationId?: string;
}

export interface CoachSession {
  token: string | null;
  email: string | null;
  team: CoachSessionTeam | null;
}

const STORAGE_KEYS = ['coach_token', 'coach_email', 'coach_team'] as const;
const STORAGE_EVENT = 'coach-session-change';

function readSession(): CoachSession {
  if (typeof window === 'undefined') {
    return { token: null, email: null, team: null };
  }
  const token = window.localStorage.getItem('coach_token');
  const email = window.localStorage.getItem('coach_email');
  const teamRaw = window.localStorage.getItem('coach_team');
  let team: CoachSessionTeam | null = null;
  if (teamRaw) {
    try {
      team = JSON.parse(teamRaw) as CoachSessionTeam;
    } catch {
      team = null;
    }
  }
  return { token, email, team };
}

/**
 * Cache the last snapshot so useSyncExternalStore sees a stable reference
 * when nothing relevant changed (required to avoid infinite re-renders).
 */
let cachedSnapshot: CoachSession = readSession();

function refreshSnapshot(): CoachSession {
  const next = readSession();
  if (
    cachedSnapshot.token !== next.token ||
    cachedSnapshot.email !== next.email ||
    cachedSnapshot.team?.id !== next.team?.id ||
    cachedSnapshot.team?.tournamentId !== next.team?.tournamentId
  ) {
    cachedSnapshot = next;
  }
  return cachedSnapshot;
}

function subscribe(notify: () => void) {
  const onStorage = (event: StorageEvent) => {
    if (!event.key || (STORAGE_KEYS as readonly string[]).includes(event.key)) {
      refreshSnapshot();
      notify();
    }
  };
  const onCustom = () => {
    refreshSnapshot();
    notify();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(STORAGE_EVENT, onCustom);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(STORAGE_EVENT, onCustom);
  };
}

function getSnapshot() {
  return cachedSnapshot;
}

function getServerSnapshot(): CoachSession {
  return { token: null, email: null, team: null };
}

function emit() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(STORAGE_EVENT));
}

export function useCoachSession() {
  const session = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const signOut = useCallback(
    (opts?: { redirect?: boolean }) => {
      if (typeof window === 'undefined') return;
      for (const key of STORAGE_KEYS) window.localStorage.removeItem(key);
      emit();
      clearIdentity();
      stopCoachRecording();
      setSentryUser(null);
      queryClient.clear();
      if (opts?.redirect !== false) setLocation('/');
    },
    [queryClient, setLocation]
  );

  // Validate token on mount. If the server rejects it, sign out silently.
  useEffect(() => {
    if (!session.token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/auth/verify-coach-token?token=${encodeURIComponent(session.token!)}`,
          { credentials: 'include' }
        );
        if (cancelled) return;
        // The verify endpoint returns 400/403 for expired or tampered tokens.
        if (!res.ok && (res.status === 400 || res.status === 401 || res.status === 403)) {
          signOut({ redirect: false });
        }
      } catch {
        // Network hiccup — keep the session; a subsequent API call will surface a real failure.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session.token, signOut]);

  return {
    ...session,
    isAuthenticated: !!session.token && !!session.team,
    signOut,
  };
}
